import { useMemo } from 'react'
import { i18WithDefault as t } from '@shared/locale'
import { Box, Stack, Typography } from '@mui/material'
import { useParams } from 'react-router-dom'
import { InvoiceCard, FlexCardDisplay, PurchaseReceipt, getPoliciesData, Spacer3, allowNoPayments } from 'components-ui'
import { useRoutes } from 'app/use_routes'
import { Hooks } from 'service-api'
import { PurchaseSummarySkeleton } from './purchase_summary_skeleton'
import { MAX_WIDGET_SIZE } from '@shared/constants'
import { isUserLoggedIn } from '@providers/session/helpers'
import { useSessionContext } from '@providers/session'

export const PurchaseSummary = () => {
  const sessionCtx = useSessionContext()
  const { navigateRoute } = useRoutes()
  const { accountName = '', bookingId = '' } = useParams()

  const {
    data: order,
    isFetching: isOrderLoading,
    refetch: refetchOrder,
  } = Hooks.useClientOrder(accountName, bookingId)
  const {
    data: receipt,
    isFetching: isReceiptLoading,
    refetch: refetchReceipt,
  } = Hooks.useClientReceipt(accountName, bookingId)
  const noPaymentsAllowed = allowNoPayments(order?.primaryServiceType?.locationBookingConf?.[0])
  const policyCardsData = useMemo(
    () => (receipt ? getPoliciesData(receipt, noPaymentsAllowed) : []),
    [receipt, noPaymentsAllowed]
  )

  if (!isUserLoggedIn(sessionCtx.state.user)) {
    navigateRoute(
      'signIn',
      {},
      {
        state: {
          redirect: {
            route: 'purchaseSummary',
            params: { bookingId },
          },
        },
      }
    )
  }

  if (isOrderLoading || isReceiptLoading) {
    return <PurchaseSummarySkeleton />
  }

  return (
    <Stack spacing={3} sx={{ maxWidth: MAX_WIDGET_SIZE, p: 3, pb: '130px', margin: '0 auto' }}>
      <Box>
        <Spacer3 />
        <Typography variant="h4">{t('purchaseSummary.title')}</Typography>
      </Box>
      <Stack spacing={3}>
        {order &&
          (order.invoices || []).map((invoice) => (
            <InvoiceCard
              key={invoice.id}
              invoice={invoice}
              order={order}
              onCancelInvoice={() => {
                refetchOrder()
                refetchReceipt()
              }}
            />
          ))}
      </Stack>
      <Stack direction="row" flexWrap="wrap" gap={1}>
        <Stack spacing={3} sx={{ flex: '1 0 49%' }}>
          {policyCardsData.map((group, index) => (
            <FlexCardDisplay key={index} cardData={group} />
          ))}
        </Stack>
        <Box sx={{ flex: '1 0 50%', height: 'fit-content' }}>
          <PurchaseReceipt receipt={receipt} />
        </Box>
      </Stack>
    </Stack>
  )
}
